import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { ContentMedia } from '../../types';

type MediaLightboxModalProps = {
  mediaItems: ContentMedia[];
  currentIndex: number;
  onChangeIndex: (_nextIndex: number) => void;
  onClose: () => void;
};

export function MediaLightboxModal({
  mediaItems,
  currentIndex,
  onChangeIndex,
  onClose,
}: MediaLightboxModalProps) {
  const mediaCount = mediaItems.length;
  const currentMedia = mediaItems[currentIndex];
  const canNavigate = mediaCount > 1;

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (!canNavigate) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        onChangeIndex((currentIndex - 1 + mediaCount) % mediaCount);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        onChangeIndex((currentIndex + 1) % mediaCount);
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [canNavigate, currentIndex, mediaCount, onChangeIndex, onClose]);

  if (!currentMedia) {
    return null;
  }

  const content = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal cdw-lightbox-modal" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header cdw-lightbox-modal__header">
          <div className="cdw-lightbox-modal__meta">
            <p className="cdw-lightbox-modal__eyebrow">Full media view</p>
            <h2>
              Slide {Math.min(currentIndex + 1, mediaCount)}
              {mediaCount > 1 ? ` / ${mediaCount}` : ''}
            </h2>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close full image view">
            ×
          </button>
        </div>

        <div className="modal-body cdw-lightbox-modal__body">
          {currentMedia.media_url ? (
            <img
              src={currentMedia.media_url}
              alt={`Slide ${Math.min(currentIndex + 1, mediaCount)} full view`}
              className="cdw-lightbox-modal__image"
            />
          ) : (
            <div className="cdw-lightbox-modal__empty">No image available for this slide.</div>
          )}

          {canNavigate && (
            <>
              <button
                className="cdw-lightbox-modal__nav cdw-lightbox-modal__nav--left"
                onClick={() => onChangeIndex((currentIndex - 1 + mediaCount) % mediaCount)}
                aria-label="Show previous slide"
              >
                <span aria-hidden="true">‹</span>
              </button>
              <button
                className="cdw-lightbox-modal__nav cdw-lightbox-modal__nav--right"
                onClick={() => onChangeIndex((currentIndex + 1) % mediaCount)}
                aria-label="Show next slide"
              >
                <span aria-hidden="true">›</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
